import React, { useEffect, useMemo, useState } from "react";

import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import SearchIcon from "@mui/icons-material/Search";
import TableRowsOutlinedIcon from "@mui/icons-material/TableRowsOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import OutlinedInput from "@mui/material/OutlinedInput";
import { useTheme } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import useMediaQuery from "@mui/material/useMediaQuery";

import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import getDestinationIcon from "helpers/destinations/getDestinationIcon";

import EmptyState from "components/atoms/EmptyState";
import PageContainer from "components/atoms/PageContainer";
import LazyScrollList from "components/organisms/LazyScrollList";

import StandartButton from "../../components/atoms/StandartButton";
import { ButtonVariant } from "../../Constant/button";
import { useDebounce } from "../../hooks/useDebounce";
import { getAllDestinationsRedux } from "../../redux/slices/destinations/destinations.asyncActions";
import { getAllSourcesRedux } from "../../redux/slices/sources/sources.asyncActions";
import {
    getExcistTrackersRedux,
    getExcistTrackersWithFilterRedux,
} from "../../redux/slices/trackers/trackers.asyncActions";
import {
    deleteLastAddedDest,
    deleteLastAddedSource,
} from "../../redux/slices/wizardSourceDestination/wizardSourceDestination.slice";
import FlowDiagram from "../Home/FlowDiagram";
import {
    MobileSegmentedControl,
    MobileSegmentTab,
} from "../Home/FlowDiagram.styled";
import DeleteTrackerDialog from "./DeleteTrackerDialog";
import TrackerActionsMenu from "./TrackerActionsMenu";
import "./trackers.css";
import {
    ActionButtonRow,
    ClearFiltersLink,
    FilterCard,
    FilterHeaderRow,
    FilterOptionIcon,
    FilterSelect,
    FilterSelectControl,
    FilterSelectLabel,
    SearchBar,
    SelectedFiltersRow,
    SelectValueText,
    TrackerBlock,
    TrackerNameLabel,
    TrackerNameValue,
    TrackerRowHeader,
    ViewToggleButton,
    ViewToggleGroup,
} from "./TrackersFlow.styled";
import TrackerTable from "./TrackerTable";

const TRACKERS_VIEW_MODE_KEY = "trackers_view_mode";
const FLOW_VIEW_MAX_TRACKER_COUNT = 5;

const readStoredTrackerViewMode = () => {
    const stored = window.localStorage.getItem(TRACKERS_VIEW_MODE_KEY);
    return stored === "table" || stored === "flow" ? stored : null;
};

const Trackers = () => {
    let [searchParams] = useSearchParams();
    const filter_completed = searchParams.get("filter_completed");
    const filter_expired = searchParams.get("filter_expired");
    const isFilterMode = useMemo(
        () => filter_completed || filter_expired,
        [filter_completed, filter_expired],
    );

    const dispatch = useDispatch();
    const trackers = useSelector((state) => state.allTrackersState.trackers);
    const loading = useSelector((state) => state.allTrackersState.status);

    const trackersWithFilter = useSelector(
        (state) => state.allTrackersState.trackersWithFilter,
    );
    const trackersWithFilterLoading = useSelector(
        (state) => state.allTrackersState.trackersWithFilterStatus,
    );

    const language = useSelector((state) => state.languagesState.languages);
    const history = useNavigate();
    const [trackersCheckStatu, setTrackersCheckStatu] = useState(false);
    const [trackersLoaderStatu, setTrackersLoaderStatu] = useState(false);
    const [deleteModalShow, setDeleteModalShow] = useState(false);
    const [confirmDeleteState, setConfirmDeleteState] = useState(null);

    const [manualViewMode, setManualViewMode] = useState(
        readStoredTrackerViewMode,
    );
    const totalTrackerCount = Array.isArray(trackers) ? trackers.length : 0;
    const viewMode =
        manualViewMode ??
        (totalTrackerCount > FLOW_VIEW_MAX_TRACKER_COUNT ? "table" : "flow");
    const selectViewMode = (mode) => {
        setManualViewMode(mode);
        window.localStorage.setItem(TRACKERS_VIEW_MODE_KEY, mode);
    };
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));

    const showTableView = viewMode === "table" || isMobileOrTablet;

    const [nameSearch, setNameSearch] = useState("");
    const debouncedNameSearch = useDebounce(nameSearch, 300);
    const [selectedSourceTypes, setSelectedSourceTypes] = useState(
        () => new Set(),
    );
    const [selectedDestinationTypes, setSelectedDestinationTypes] = useState(
        () => new Set(),
    );

    const hasActiveFilters =
        !!nameSearch ||
        selectedSourceTypes.size > 0 ||
        selectedDestinationTypes.size > 0;

    const clearFilters = () => {
        setNameSearch("");
        setSelectedSourceTypes(new Set());
        setSelectedDestinationTypes(new Set());
    };

    const removeSourceType = (id) => {
        setSelectedSourceTypes((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const removeDestinationType = (id) => {
        setSelectedDestinationTypes((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const visibleTrackers = useMemo(() => {
        const list = isFilterMode ? trackersWithFilter : trackers;
        const query = debouncedNameSearch.trim().toLowerCase();
        return list?.filter((tracker) => {
            if (
                query &&
                !tracker?.tracker_name?.toLowerCase().includes(query)
            ) {
                return false;
            }
            if (
                selectedSourceTypes.size > 0 &&
                !tracker?.sources?.some((source) =>
                    selectedSourceTypes.has(source.source_type_id),
                )
            ) {
                return false;
            }
            if (
                selectedDestinationTypes.size > 0 &&
                !tracker?.destinations?.some((destination) =>
                    selectedDestinationTypes.has(
                        destination.destination_type_id,
                    ),
                )
            ) {
                return false;
            }
            return true;
        });
    }, [
        isFilterMode,
        trackersWithFilter,
        trackers,
        debouncedNameSearch,
        selectedSourceTypes,
        selectedDestinationTypes,
    ]);

    const sources = useSelector(
        (state) => state.allSourcesState.getAllSources.sources,
    );
    const destinations = useSelector(
        (state) => state.allDestinationsState.getAllDestinations.destinations,
    );

    useEffect(() => {
        return () => {
            dispatch(deleteLastAddedDest());
            dispatch(deleteLastAddedSource());
        };
    }, []);

    useEffect(() => {
        const filterPayload = {
            ...(filter_completed ? { filter_completed } : {}),
            ...(filter_expired ? { filter_expired } : {}),
        };
        const fetchAction = Object.keys(filterPayload).length
            ? getExcistTrackersWithFilterRedux
            : getExcistTrackersRedux;
        dispatch(fetchAction(filterPayload));
        setTrackersCheckStatu(true);
        dispatch(getAllSourcesRedux());
        dispatch(getAllDestinationsRedux());
    }, [filter_completed, filter_expired]);

    useEffect(() => {
        const isLoading = loading === "loading" || loading === "idle";
        const withFilterIsLoading =
            trackersWithFilterLoading === "loading" ||
            trackersWithFilterLoading === "idle";

        setTrackersLoaderStatu(isFilterMode ? withFilterIsLoading : isLoading);
    }, [loading, trackersWithFilterLoading]);
    const getSingleTracker = (id) => {
        history(`/tracker/${id}`);
    };

    const handleCreateButton = () => {
        history(`/wizard`);
    };

    const getSourceIcon = (id) => {
        let _sources = [...sources];
        let code = _sources.find((x) => x.source_type_id === id)?.source_type
            ?.code;
        if (code !== undefined) {
            let imageSrc = "";
            switch (code) {
                case "js":
                    imageSrc = "/assets/images/typeImages/web-icon.png";
                    break;
                case "redirect":
                    imageSrc = "/assets/images/typeImages/redirect.png";
                    break;
                case "capi":
                    imageSrc = "/assets/images/typeImages/offline-icon.webp";
                    break;
                case "dynamics":
                    imageSrc =
                        "/assets/images/typeImages/microsoft-dynamics-logo.png";
                    break;
                case "ftp":
                    imageSrc = "/assets/images/typeImages/ftp-icon.png";
                    break;
                case "sftp":
                    imageSrc = "/assets/images/typeImages/sftp-icon.png";
                    break;
                case "salesforce":
                    imageSrc = "/assets/images/typeImages/sales.png";
                    break;
                case "zoho":
                    imageSrc = "/assets/images/typeImages/zoho.png";
                    break;
                case "hub":
                    imageSrc = "/assets/images/typeImages/hubspot-icon.png";
                    break;
                case "monday":
                    imageSrc = "/assets/images/typeImages/monday.png";
                    break;
                case "zendesk_sell":
                    imageSrc = "/assets/images/typeImages/zendesk-icon.png";
                    break;
                case "pipe":
                    imageSrc = "/assets/images/typeImages/pipedrive.png";
                    break;
                case "b24":
                    imageSrc = "/assets/images/typeImages/b24.png";
                    break;
                case "upload":
                    imageSrc = "/assets/images/typeImages/file-upload-icon.png";
                    break;
                case "spreadsheet":
                    imageSrc =
                        "/assets/images/typeImages/google-sheets-icon.png";
                    break;
                case "freshsales":
                    imageSrc = "/assets/images/typeImages/freshsales-crm.png";
                    break;
                case "vtiger":
                    imageSrc = "/assets/images/typeImages/vtiger-crm.png";
                    break;
                case "odoo":
                    imageSrc = "/assets/images/typeImages/odoo.png";
                    break;
                case "lsqrd":
                    imageSrc = "/assets/images/typeImages/leadsquared.png";
                    break;
                case "appsflyer":
                    imageSrc = "/assets/images/typeImages/appsflyer.png";
                    break;
                case "adjust":
                    imageSrc = "/assets/images/typeImages/adjust.png";
                    break;
                case "bigquery":
                    imageSrc = "/assets/images/typeImages/bigquery.png";
                    break;
                case "androidcapi":
                    imageSrc = "/assets/images/typeImages/androidcapi.png";
                    break;
                case "ioscapi":
                    imageSrc = "/assets/images/typeImages/ioscapi.png";
                    break;
                case "msg":
                    imageSrc = "/assets/images/typeImages/messaging.png";
                    break;

                default:
                    imageSrc = "";
            }
            return imageSrc;
        }
    };

    const getDestinationIconHandler = (id) => {
        let _destinations = [...destinations];
        let code = _destinations.find((x) => x.destination_type_id === id)
            ?.destination_type?.code;
        if (code) {
            return getDestinationIcon(code);
        }
    };

    const sourceTypeOptions = useMemo(() => {
        const map = new Map();
        (sources || []).forEach((source) => {
            if (source?.source_type_id == null) return;
            if (!map.has(source.source_type_id)) {
                map.set(source.source_type_id, {
                    id: source.source_type_id,
                    name: source?.source_type?.name,
                    icon: getSourceIcon(source.source_type_id),
                });
            }
        });
        return Array.from(map.values());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [sources]);

    const destinationTypeOptions = useMemo(() => {
        const map = new Map();
        (destinations || []).forEach((destination) => {
            if (destination?.destination_type_id == null) return;
            if (!map.has(destination.destination_type_id)) {
                map.set(destination.destination_type_id, {
                    id: destination.destination_type_id,
                    name: destination?.destination_type?.name,
                    icon: getDestinationIconHandler(
                        destination.destination_type_id,
                    ),
                });
            }
        });
        return Array.from(map.values());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [destinations]);

    const emptyState = hasActiveFilters ? (
        <EmptyState
            icon={<SearchIcon />}
            title={language?.trackers?.no_trackers_found ?? "No trackers found"}
            description={
                language?.trackers?.no_trackers_found_filtered_desc ??
                "No trackers match your current filters. Try adjusting or clearing them to see more results."
            }
        />
    ) : (
        <EmptyState
            icon={<AccountTreeOutlinedIcon />}
            title={language?.trackers?.no_trackers_yet ?? "No trackers yet"}
            description={
                language?.trackers?.no_trackers_yet_desc ??
                "Create your first tracker to start connecting your sources and destinations."
            }
        />
    );

    return (
        <PageContainer
            containerClassName="tracker-detail-area"
            title={language?.trackers?.trackers ?? "Trackers"}
            subtitle={
                language?.trackers?.trackers_desc ??
                "On this page, you can view trackers, and create a new one by using the button."
            }
            additionalButtons={
                <Button
                    onClick={handleCreateButton}
                    color="primary"
                    variant="contained"
                    data-cy="trackers-create"
                >
                    {"Create Tracker"}
                </Button>
            }
            loading={loading === "idle" || loading === "loading"}
            variant="list"
        >
            <FilterCard>
                <FilterHeaderRow>
                    <SearchBar>
                        <TextField
                            fullWidth
                            size="small"
                            variant="outlined"
                            placeholder={
                                language?.trackers?.search_by_name ??
                                "Search by tracker name..."
                            }
                            value={nameSearch}
                            onChange={(e) => setNameSearch(e.target.value)}
                            inputProps={{ "data-cy": "trackers-search" }}
                            InputProps={{
                                startAdornment: (
                                    <SearchIcon
                                        sx={{
                                            mr: 1,
                                            color: "action.active",
                                            fontSize: 20,
                                        }}
                                    />
                                ),
                            }}
                        />
                    </SearchBar>

                    {sourceTypeOptions.length > 0 && (
                        <FilterSelectControl size="small">
                            <FilterSelectLabel id="sources-filter-label" shrink>
                                {language?.trackers?.sources_title ?? "Sources"}
                            </FilterSelectLabel>
                            <FilterSelect
                                labelId="sources-filter-label"
                                size="small"
                                multiple
                                displayEmpty
                                notched
                                input={
                                    <OutlinedInput
                                        notched
                                        label={
                                            language?.trackers?.sources_title ??
                                            "Sources"
                                        }
                                    />
                                }
                                value={Array.from(selectedSourceTypes)}
                                onChange={(e) =>
                                    setSelectedSourceTypes(
                                        new Set(e.target.value),
                                    )
                                }
                                renderValue={(selected) => {
                                    const text =
                                        selected.length === 0
                                            ? (language?.trackers
                                                  ?.all_sources ??
                                              "Select Sources")
                                            : sourceTypeOptions
                                                  .filter((option) =>
                                                      selected.includes(
                                                          option.id,
                                                      ),
                                                  )
                                                  .map((option) => option.name)
                                                  .join(", ");
                                    return (
                                        <SelectValueText title={text}>
                                            {text}
                                        </SelectValueText>
                                    );
                                }}
                            >
                                {sourceTypeOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        <Checkbox
                                            size="small"
                                            checked={selectedSourceTypes.has(
                                                option.id,
                                            )}
                                            sx={{
                                                p: 0.5,
                                                color: "#CBD5E1",
                                                "&.Mui-checked": {
                                                    color: "#ed6c02",
                                                },
                                            }}
                                        />
                                        <FilterOptionIcon>
                                            {option.icon && (
                                                <img src={option.icon} alt="" />
                                            )}
                                        </FilterOptionIcon>
                                        <ListItemText primary={option.name} />
                                    </MenuItem>
                                ))}
                            </FilterSelect>
                        </FilterSelectControl>
                    )}

                    {destinationTypeOptions.length > 0 && (
                        <FilterSelectControl size="small">
                            <FilterSelectLabel
                                id="destinations-filter-label"
                                shrink
                            >
                                {language?.trackers?.destinations_title ??
                                    "Destinations"}
                            </FilterSelectLabel>
                            <FilterSelect
                                labelId="destinations-filter-label"
                                size="small"
                                multiple
                                displayEmpty
                                notched
                                input={
                                    <OutlinedInput
                                        notched
                                        label={
                                            language?.trackers
                                                ?.destinations_title ??
                                            "Destinations"
                                        }
                                    />
                                }
                                value={Array.from(selectedDestinationTypes)}
                                onChange={(e) =>
                                    setSelectedDestinationTypes(
                                        new Set(e.target.value),
                                    )
                                }
                                renderValue={(selected) => {
                                    const text =
                                        selected.length === 0
                                            ? (language?.trackers
                                                  ?.all_destinations ??
                                              "Select Destinations")
                                            : destinationTypeOptions
                                                  .filter((option) =>
                                                      selected.includes(
                                                          option.id,
                                                      ),
                                                  )
                                                  .map((option) => option.name)
                                                  .join(", ");
                                    return (
                                        <SelectValueText title={text}>
                                            {text}
                                        </SelectValueText>
                                    );
                                }}
                            >
                                {destinationTypeOptions.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                        <Checkbox
                                            size="small"
                                            checked={selectedDestinationTypes.has(
                                                option.id,
                                            )}
                                            sx={{
                                                p: 0.5,
                                                color: "#CBD5E1",
                                                "&.Mui-checked": {
                                                    color: "#ed6c02",
                                                },
                                            }}
                                        />
                                        <FilterOptionIcon>
                                            {option.icon && (
                                                <img src={option.icon} alt="" />
                                            )}
                                        </FilterOptionIcon>
                                        <ListItemText primary={option.name} />
                                    </MenuItem>
                                ))}
                            </FilterSelect>
                        </FilterSelectControl>
                    )}

                    {hasActiveFilters && (
                        <ClearFiltersLink onClick={clearFilters}>
                            {language?.trackers?.clear_filters ??
                                "Clear filters"}
                        </ClearFiltersLink>
                    )}

                    {!isMobileOrTablet && (
                        <ViewToggleGroup>
                            <ViewToggleButton
                                $active={viewMode === "flow"}
                                onClick={() => selectViewMode("flow")}
                            >
                                <AccountTreeOutlinedIcon />
                                {language?.trackers?.flow_view ?? "Flow"}
                            </ViewToggleButton>
                            <ViewToggleButton
                                $active={viewMode === "table"}
                                onClick={() => selectViewMode("table")}
                            >
                                <TableRowsOutlinedIcon />
                                {language?.trackers?.table_view ?? "Table"}
                            </ViewToggleButton>
                        </ViewToggleGroup>
                    )}
                </FilterHeaderRow>

                {(selectedSourceTypes.size > 0 ||
                    selectedDestinationTypes.size > 0) && (
                    <SelectedFiltersRow>
                        {sourceTypeOptions
                            .filter((option) =>
                                selectedSourceTypes.has(option.id),
                            )
                            .map((option) => (
                                <Chip
                                    key={`source-${option.id}`}
                                    size="small"
                                    label={option.name}
                                    avatar={
                                        option.icon ? (
                                            <Avatar src={option.icon} alt="" />
                                        ) : undefined
                                    }
                                    onClick={() => removeSourceType(option.id)}
                                    onDelete={() => removeSourceType(option.id)}
                                />
                            ))}
                        {destinationTypeOptions
                            .filter((option) =>
                                selectedDestinationTypes.has(option.id),
                            )
                            .map((option) => (
                                <Chip
                                    key={`destination-${option.id}`}
                                    size="small"
                                    label={option.name}
                                    avatar={
                                        option.icon ? (
                                            <Avatar src={option.icon} alt="" />
                                        ) : undefined
                                    }
                                    onClick={() =>
                                        removeDestinationType(option.id)
                                    }
                                    onDelete={() =>
                                        removeDestinationType(option.id)
                                    }
                                />
                            ))}
                    </SelectedFiltersRow>
                )}
            </FilterCard>

            {showTableView ? (
                <TrackerTable
                    tableName="trackers"
                    trackers={visibleTrackers}
                    getSourceIcon={getSourceIcon}
                    getDestinationIcon={getDestinationIconHandler}
                    onNavigateTracker={getSingleTracker}
                    onDeleteTracker={(tracker) => {
                        setConfirmDeleteState(tracker);
                        setDeleteModalShow(true);
                    }}
                    language={language}
                    trackerNameTitle={
                        language?.trackers?.tracker_name ?? "Tracker Name"
                    }
                    sourcesTitle={
                        language?.trackers?.sources_title ?? "Sources"
                    }
                    destinationsTitle={
                        language?.trackers?.destinations_title ?? "Destinations"
                    }
                    statusTitle={language?.trackers?.status ?? "Status"}
                    healthyLabel={language?.trackers?.healthy ?? "Healthy"}
                    attentionLabel={
                        language?.trackers?.needs_attention ?? "Needs attention"
                    }
                    idleLabel={
                        language?.trackers?.no_connections ?? "No connections"
                    }
                    noSourcesLabel={language?.trackers?.none ?? "None"}
                    noDestinationsLabel={language?.trackers?.none ?? "None"}
                    viewLabel={language?.trackers?.view ?? "View"}
                    emptyStateLabel={emptyState}
                />
            ) : (
                <div className="trackers-area">
                    {isMobile && (
                        <MobileSegmentedControl>
                            {[
                                language?.trackers?.sources_title ?? "Sources",
                                "Name",
                                language?.trackers?.destinations_title ??
                                    "Destinations",
                            ].map((label) => (
                                <MobileSegmentTab key={label}>
                                    {label}
                                </MobileSegmentTab>
                            ))}
                        </MobileSegmentedControl>
                    )}
                    <LazyScrollList
                        items={visibleTrackers ?? []}
                        getItemKey={(tracker) => tracker?.id}
                        initialCount={5}
                        batchSize={5}
                        emptyState={emptyState}
                        renderItem={(tracker) => (
                            <TrackerBlock key={tracker?.id}>
                                <FlowDiagram
                                    trackers={[tracker]}
                                    compact
                                    getSourceIcon={getSourceIcon}
                                    getDestinationIcon={
                                        getDestinationIconHandler
                                    }
                                    onNavigateTracker={() =>
                                        getSingleTracker(tracker?.id)
                                    }
                                    onHubClick={() =>
                                        getSingleTracker(tracker?.id)
                                    }
                                    onAddSource={() =>
                                        getSingleTracker(tracker?.id)
                                    }
                                    onAddDestination={() =>
                                        getSingleTracker(tracker?.id)
                                    }
                                    sourcesTitle={
                                        language?.trackers?.sources_title ??
                                        "Sources"
                                    }
                                    destinationsTitle={
                                        language?.trackers
                                            ?.destinations_title ??
                                        "Destinations"
                                    }
                                    addSourceLabel={
                                        language?.dashboard?.add_source ??
                                        "Add Source"
                                    }
                                    addDestinationLabel={
                                        language?.dashboard?.add_destination ??
                                        "Add Destination"
                                    }
                                    hideMobileSegmentedControl
                                    header={
                                        <TrackerRowHeader>
                                            <TrackerNameLabel>
                                                {(language?.trackers
                                                    ?.tracker_name ??
                                                    "Tracker Name") + ":"}
                                                <TrackerNameValue
                                                    component={Link}
                                                    to={`/tracker/${tracker?.id}`}
                                                >
                                                    {tracker?.tracker_name}
                                                </TrackerNameValue>
                                            </TrackerNameLabel>
                                            <ActionButtonRow>
                                                <StandartButton
                                                    variant={
                                                        ButtonVariant.BLACK_OUTLINED
                                                    }
                                                    startIcon={
                                                        <VisibilityIcon />
                                                    }
                                                    to={`/tracker/${tracker?.id}`}
                                                >
                                                    {language?.trackers?.view ??
                                                        "View"}
                                                </StandartButton>
                                                <TrackerActionsMenu
                                                    settingsTo={`/tracker-setting/${tracker?.id}`}
                                                    reportsTo={`/tracker-reports/${tracker?.id}`}
                                                    onDelete={() => {
                                                        setConfirmDeleteState(
                                                            tracker,
                                                        );
                                                        setDeleteModalShow(
                                                            true,
                                                        );
                                                    }}
                                                    language={language}
                                                />
                                            </ActionButtonRow>
                                        </TrackerRowHeader>
                                    }
                                />
                            </TrackerBlock>
                        )}
                    />
                </div>
            )}

            <DeleteTrackerDialog
                open={deleteModalShow}
                onClose={() => setDeleteModalShow(false)}
                tracker={confirmDeleteState}
                language={language}
                onDeleteSuccess={() => dispatch(getExcistTrackersRedux())}
            />
        </PageContainer>
    );
};

export default Trackers;
